<template>
  <div class="p-6">
    <div class="mb-6">
      <h2
        class="text-gray-800 text-xl font-bold tracking-tight"
        v-text="t('messages.my_tool_box')"
      />
      <div class="mt-1 h-0.5 w-12 bg-gradient-to-r from-primary to-indigo-400 rounded-full" />
    </div>
    
    <ul
      role="list"
      class="space-y-3"
    >
      <template
        v-for="(item, itemIdx) in availableTools"
        :key="itemIdx"
      >
        <li v-if="itemIdx < 2 || shouldShowAllSidebarTools">
          <div class="relative group bg-white/80 backdrop-blur-sm rounded-xl p-4 hover:shadow-md transition-all duration-200 border border-gray-200/40 hover:border-gray-300/60">
            <div class="flex items-start gap-3">
              <div class="flex-shrink-0">
                <div 
                  :class="[
                    item.iconColor, 
                    'inline-flex items-center justify-center h-12 w-12 rounded-xl shadow-sm group-hover:shadow-md transition-shadow duration-200'
                  ]"
                >
                  <component
                    :is="item.icon"
                    class="h-6 w-6 text-white drop-shadow-sm"
                    aria-hidden="true"
                  />
                </div>
              </div>
              
              <div class="min-w-0 flex-1">
                <div class="text-sm font-bold text-gray-900 leading-tight">
                  <Link
                    v-if="item.inertiaPage"
                    :href="item.href"
                    class="hover:text-primary transition-colors duration-200"
                  >
                    <span
                      class="absolute inset-0"
                      aria-hidden="true"
                    />
                    {{ item.name }}
                  </Link>
                  <a
                    v-else
                    :href="item.href"
                    class="hover:text-primary transition-colors duration-200"
                  >
                    <span
                      class="absolute inset-0"
                      aria-hidden="true"
                    />
                    {{ item.name }}
                  </a>
                </div>
                <p class="text-xs text-gray-600 mt-1 line-clamp-2 leading-relaxed">
                  {{ item.description }}
                </p>
              </div>
              
              <div class="flex-shrink-0 self-center">
                <ChevronRightIcon
                  class="h-4 w-4 text-gray-400 group-hover:text-primary group-hover:translate-x-1 transition-all duration-200"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        </li>
      </template>
      
      <li>
        <button
          class="w-full p-3 text-primary hover:text-indigo-600 inline-flex justify-center items-center gap-2 rounded-xl hover:bg-primary/5 transition-all duration-200 font-medium text-sm"
          @click.prevent="store.toggleShowAllSidebarTools"
        >
          <span
            v-if="shouldShowAllSidebarTools"
            v-text="t('messages.view_less')"
          />
          <span
            v-else
            v-text="t('messages.view_more')"
          />
          <ChevronUpIcon
            v-if="shouldShowAllSidebarTools"
            class="h-4 w-4 transition-transform duration-200"
          />
          <ChevronDownIcon
            v-else
            class="h-4 w-4 transition-transform duration-200"
          />
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import {
  BanknotesIcon, BoltIcon, ChartPieIcon,
  CurrencyEuroIcon, PhoneIcon, ShieldCheckIcon,
  SparklesIcon,
} from '@heroicons/vue/24/outline';
import {
  BanknotesIcon as BanknotesIconSolid,
  ChevronRightIcon,
  ShieldCheckIcon as ShieldCheckIconSolid,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/vue/24/solid';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useMainStore } from '@/stores/main';
import useBackendRouting from '@/composables/useBackendRouting';
import useAppData from '@/composables/useAppData';
import { Link } from '@inertiajs/vue3';

const { t } = useI18n();

const { route } = useBackendRouting();

const store = useMainStore();

const { shouldShowAllSidebarTools } = storeToRefs(store);

const {
  toolsFeatures,
} = useAppData();

const tools = [
  {
    name: t('Flash Compliance Audit'),
    description: t('Get a clear overview of your regulatory compliance in just a few clicks. A fast, practical, and essential tool for anticipating gaps and taking effective action.'),
    href: route('broker_dashboard.tools.quick_audit.show'),
    iconColor: 'bg-yellow-500 premium-shine',
    icon: BoltIcon,
    active: toolsFeatures.value['fr-flash-compliance-audit-tool'] ?? false,
    inertiaPage: true,
  },
  {
    name: t('Telephone canvassing compliance diagnosis'),
    description: t('Instantly get your cold calling compliance score and identify areas of vigilance to address.'),
    href: route('broker_dashboard.tools.phone_call_audit_request.show'),
    iconColor: 'bg-yellow-500',
    icon: PhoneIcon,
    active: toolsFeatures.value['fr-phone-call-compliance-audit-request-tool'] ?? false,
    inertiaPage: true,
  },
  {
    name: t('messages.probability_of_finding_a_portfolio'),
    description: t('messages.probability_of_finding_a_portfolio_nav_description'),
    href: route('tools.probability_of_finding_portfolio'),
    iconColor: 'bg-pink-600',
    icon: ChartPieIcon,
    active: toolsFeatures.value['fr-portfolio-probability-tool'] ?? false,
    inertiaPage: false,
  },
  {
    name: t('messages.how_much_can_expect_to_receive'),
    description: t('messages.how_much_can_expect_to_receive_nav_description'),
    href: route('tools.expected_income'),
    iconColor: 'bg-pink-600',
    icon: BanknotesIconSolid,
    active: toolsFeatures.value['fr-expected-income-tool'] ?? false,
    inertiaPage: false,
  },
  {
    name: t('messages.guide_april'),
    description: t('messages.guide_april_description'),
    href: route('tools.april_guide'),
    iconColor: 'bg-pink-600',
    icon: ShieldCheckIconSolid,
    active: toolsFeatures.value['april-guide-tool'] ?? false,
    inertiaPage: false,
  },
  {
    name: t('Les étoiles du courtage'),
    description: t('Discover and reference initiatives that move insurance brokerage forward.'),
    href: route('tools.brokerage_stars'),
    iconColor: 'bg-amber-500',
    icon: SparklesIcon,
    active: toolsFeatures.value['fr-brokerage-stars-tool'] ?? false,
    inertiaPage: false,
  },
  {
    name: t('messages.how_much_should_i_invest'),
    description: t('messages.how_much_should_i_invest_nav_description'),
    href: route('tools.expected_investment'),
    iconColor: 'bg-pink-600',
    icon: BanknotesIcon,
    active: toolsFeatures.value['fr-expected-investment-tool'] ?? false,
    inertiaPage: false,
  },
  {
    name: t('messages.guide_become_a_broker'),
    description: t('messages.guide_become_a_broker_description'),
    href: route('tools.guide_become_a_broker'),
    iconColor: 'bg-pink-600',
    icon: ShieldCheckIcon,
    active: toolsFeatures.value['brokerage-guide-tool'] ?? false,
    inertiaPage: false,
  },
  {
    name: t('messages.cost_of_a_purchase'),
    description: t('messages.cost_of_a_purchase_nav_description'),
    href: route('tools.cost_of_purchase'),
    iconColor: 'bg-pink-600',
    icon: CurrencyEuroIcon,
    active: toolsFeatures.value['fr-cost-of-a-purchase-tool'] ?? false,
    inertiaPage: false,
  },
];

const availableTools = computed(() => tools.filter(tool => tool.active));
</script>
